import React from "react";

import { Box, Button, Container, Typography } from "@material-ui/core";

import { useTranslation } from "react-i18next";

import { useNavigate } from "react-router";

import { AvailableOrderTypes } from "api/types/enums";
import makeStyles from "hooks/makeStyles";
import useBusiness from "hooks/useBusiness";
import useCart from "hooks/useCart";
import CartIcon from "icons/CartIcon";
import theme from "styles/theme";
import { formatMoney } from "utility/formatMoney";

const useStyles = makeStyles(theme => ({
	root: {
		borderTop: "1px solid #888",
		padding: 0,
		paddingTop: theme.spacing(2),
		display: "flex",
		flex: 1,
		flexDirection: "column",
		position: "relative"
	},
	title: {
		textAlign: "left",
		textTransform: "uppercase",
		fontWeight: 500,
		fontSize: 12,
		letterSpacing: "0.38px",
		alignContent: "center",
		alignItems: "center"
	},
	titleBlock: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center"
	},
	icon: {
		height: 12,
		width: 12,
		marginRight: theme.spacing(1),
		fill: "#fff",
		[theme.breakpoints.up(480)]: {
			fill: theme.palette.text.primary
		}
	},
	change: {
		border: 0,
		padding: 0,
		fontSize: 12,
		textTransform: "uppercase",
		fontWeight: 500,
		width: "auto",
		minWidth: "auto",
		color: "#fff"
	},
	items: {
		marginBottom: theme.spacing(2)
	},
	item: {
		marginLeft: theme.spacing(2),
		marginTop: theme.spacing(2),
		marginRight: theme.spacing(2),
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between"
	},
	itemTitle: {
		color: "#f2f2f2",
		textTransform: "uppercase",
		fontSize: "12px",
		letterSpacing: "0.38px"
	},
	itemTitleChecked: {
		color: "#fff"
	},
	titleRow: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center"
	},
	dataRow: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		paddingBottom: theme.spacing(1)
	},
	data: {
		display: "inline-block",
		fontSize: 14,
		textTransform: "uppercase"
	},
	total: {
		fontSize: 16,
		textTransform: "uppercase",
		fontWeight: 500,
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		paddingBottom: theme.spacing(1)
	},
	discountText: {
		color: theme.palette.secondary.dark
	}
}));

const OrderTotal = () => {
	const classes = useStyles();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const {
		orderType,
		cartTotalCents,
		taxCents,
		grandTotalCents,
		tipPercentage,
		getTipCents,
		discount,
		percentOff
	} = useCart();
	const { business } = useBusiness();
	const { taxRate } = business;
	const onChangeClicked = () => {
		navigate("#checkout:0");
	};

	return (
		<Container className={classes.root}>
			<Box className={classes.titleRow}>
				<Box className={classes.titleBlock}>
					<CartIcon className={classes.icon} />
					<Typography className={classes.title}>{t("yourOrder")}</Typography>
				</Box>
				<Button className={classes.change} onClick={onChangeClicked}>
					{t("change")}
				</Button>
			</Box>
			<Box className={classes.dataRow}>
				<Typography className={classes.data}>{t("foodBeverageTotal")}</Typography>
				<Typography className={classes.data}>{formatMoney(cartTotalCents)}</Typography>
			</Box>
			{discount !== null && discount > 0 && (
				<Box className={`${classes.dataRow} ${classes.discountText}`}>
					<Typography className={classes.data}>{t("discountPercentOff", { percentOff }).toUpperCase()}</Typography>
					<Typography className={classes.data}>- {formatMoney(discount)}</Typography>
				</Box>
			)}
			{orderType?.orderType !== AvailableOrderTypes.delivery ? null : (
				<Box className={classes.dataRow}>
					<Typography className={classes.data}>{t("deliveryFee")}</Typography>
					<Typography className={classes.data}>{formatMoney(orderType.priceCents || 0)}</Typography>
				</Box>
			)}
			<Box className={classes.dataRow}>
				<Typography className={classes.data}>{t("tax", { taxRate })}</Typography>
				<Typography className={classes.data}>{formatMoney(taxCents)}</Typography>
			</Box>
			<Box className={classes.dataRow}>
				<Typography className={classes.data}>
					{t("tip")} {tipPercentage ? `(${tipPercentage}%)` : ""}
				</Typography>
				<Typography className={classes.data}>{formatMoney(getTipCents())}</Typography>
			</Box>
			{/* <Box className={classes.data}>{t("courrierTip")}</Box> */}
			<Box className={classes.dataRow}>
				<Typography className={classes.total}>{t("total")}</Typography>
				<Typography className={classes.total}>{formatMoney(grandTotalCents)}</Typography>
			</Box>
		</Container>
	);
};

export default OrderTotal;
